import { useLocation, useNavigate } from 'react-router';
import { CitationProgressCard } from './CitationProgressCard';
import { useEffect, useRef, useState } from 'react';
import { useGetACampaignDetails } from '../hooks';
import { useCitation } from '../Providers/CitationProvider';

const features = [
  'Create new citations',
  'Fix inaccurate or outdated listings',
  'Remove duplicates',
  'Submit to trusted data aggregators'
];

const EXPECTED_DURATIONS_IN_MILLISECONDS = 5 * 60 * 1000; // 5 minutes

export const CitationScanning = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { setCampaignDetails, setError } = useCitation();

  const { campaignId, locationId } = state as { campaignId: string, locationId: string };

  const { data: campaignData } = useGetACampaignDetails({
    campaignId,
    refetch: true
  });

  const startTimeRef = useRef<number>(Date.now());
  const [progressValue, setProgressValue] = useState(0);

  useEffect(() => {
    if (!campaignData) {
      return;
    }

    // Handle error case
    if (campaignData.status !== 200) {
      setError({
          status: 'error',
          message: 'Failed to get campaign data. Please try again.'
        });
      navigate('/local-seo/citations');
      return;
    }

    const lookupStatus = campaignData.data?.response?.lookup_status;

    // Handle completion case
    if (lookupStatus === 'complete') {
      setProgressValue(100);
      setCampaignDetails(campaignData.data?.response);
      const timeoutId = setTimeout(() => {
        navigate('/local-seo/citations/create-campaign', {
          state: { campaignId, locationId }
        });
      }, 1000);


      return () => clearTimeout(timeoutId);
    }

    // Update progress while processing
    if (lookupStatus === 'processing') {
      const elapsed = Date.now() - startTimeRef.current;
      const estimated = (elapsed / EXPECTED_DURATIONS_IN_MILLISECONDS) * 95; // stop at 95%
      setProgressValue((prev) => Math.max(prev, Math.min(estimated, 95)));
    }
  }, [campaignData, navigate, campaignId, locationId, setCampaignDetails, setError]);

  return (
    <CitationProgressCard
      topCardTitle="Let's Do The Heavy Lifting"
      topCardDescription="SEO Space is scanning your business across hundreds of citation directories to find existing listings. This usually takes a few minutes. Feel free to explore - we'll notify you in-app when your audit is ready."
      statusText="Scanning for existing citations..."
      progressValue={progressValue}
      bottomCardTitle="What's next?"
      bottomCardIntro="Once the audit is complete, you'll be able to build a tailored campaign to improve your local presence:"
      features={features}
    />
  );
};
